import { Dispatch, SetStateAction, createContext, useState, useMemo } from 'react';

import { Translations } from 'types/translation';

import translations from '../data/localizations.json';

export type TranslationContextType = {
  en: Translations;
};

interface IProps {
  children: JSX.Element;
}

const defaultLanguage = 'en';

export const TranslationContext = createContext<{
  activeLanguage: keyof TranslationContextType | undefined;
  t: (path: keyof Translations, keys?: Record<string, string>) => string;
  setActiveLanguage: Dispatch<SetStateAction<keyof TranslationContextType | undefined>>;
}>({
  activeLanguage: defaultLanguage,
  t: (_path: keyof Translations) => '',
  setActiveLanguage: () => ''
});

export const TranslationContextProvider = ({ children }: IProps): JSX.Element => {
  const [activeLanguage, setActiveLanguage] = useState<keyof TranslationContextType>();

  const translate = (path?: keyof Translations, keys?: Record<string, string>): string => {
    if (!path) {
      return 'Missing localization key!';
    }

    let translation = (translations as TranslationContextType)[activeLanguage ?? defaultLanguage]?.[path];

    if (translation && keys) {
      Object.keys(keys).map((key) => {
        const regex = new RegExp(`{${key}}`, 'gm');
        translation = typeof translation === 'string' ? translation?.replace(regex, keys[key]) : translation;
      });
    }

    return translation || path;
  };
  
  const value = useMemo(
    () => ({ activeLanguage, t: translate, setActiveLanguage }),
    [translations, activeLanguage, translate]
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};
